import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useApiOperation } from "src/api/hooks";
import { getOrgDetail } from "src/api/organization/org-api";
import { MbOrgDetail } from "src/api/organization/org-types";
import { ContentsTitle } from "src/components";
import HistoryDetail from "src/components/histroy/HistoryDetail";
import MetaTag from "src/components/layout/MetaTag";
import useNavigate from "src/hooks/usePartnerNavigate";
import PagePath from "src/pagePath.json";
import OrgBaseInfo from "./components/baseInfo/OrgBaseInfo";
import BuildingInfo from "./components/buildingInfo/BuildingInfo";
import UseProductInfo from "./components/useProductInfo/UseProductInfo";

const OrganizationDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeAsync: getOrganizationDetail } = useApiOperation(getOrgDetail);
  const [orgDetail, setOrgDetail] = useState<MbOrgDetail>();
  const { id: orgId } = useParams();

  const tabs = [
    { value: "basicInfo", label: "기본정보" },
    { value: "useProduct", label: "신청/계약" },
    { value: "buildingInfo", label: "건물" },
    { value: "history", label: "변경내역" },
  ];

  // location search (url query parameter) 를 읽어서 object 로 변환
  const queryParams = useMemo(
    () =>
      qs.parse(location.search, {
        ignoreQueryPrefix: true,
        allowDots: true,
        decoder: (value) => value, //탭 이동시 decoding
      }),
    [location],
  );

  // 현재 활성화 되어야하는 tab
  const activeTab = useMemo(() => {
    if (queryParams?.tab) {
      return tabs.find((tab) => tab.value === queryParams?.tab);
    }
    return tabs[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  // 탭 클릭시 callback 함수
  const clickTab = useCallback(
    (tab: { value: string; label: string }) => {
      const newQueryParams = { ...queryParams };
      if (newQueryParams?.tab) {
        delete newQueryParams.tab;
      }

      newQueryParams.tab = tab.value;

      // query parameter 에 tab 추가
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [navigate, location, queryParams],
  );

  useEffect(() => {
    getOrganizationDetail({ id: Number(orgId) }).then((response) => {
      setOrgDetail(response.data.data);
    });
  }, [orgId, getOrganizationDetail]);

  const breadCrumbs = [{ value: "organization", label: "사업자" }];
  if (activeTab) {
    // 마지막 bread crumb 는 현재 선택된 탭
    breadCrumbs.push({
      value: activeTab.value,
      label: activeTab.label,
    });
  }

  return (
    <div>
      <MetaTag subTitle={orgDetail?.organization.name} />
      <ContentsTitle title="" tabs={tabs} activeTab={activeTab} clickTab={clickTab} detailInfo={[{ title: "계약자 ID", value: orgId || "" }]} />

      {activeTab?.value === "basicInfo" && <OrgBaseInfo />}
      {activeTab?.value === "useProduct" && <UseProductInfo orgId={String(orgId)} />}
      {activeTab?.value === "buildingInfo" && <BuildingInfo orgId={String(orgId)} />}
      {activeTab?.value === "history" && orgId && <HistoryDetail identifier={{ organizationId: String(orgId) }} />}
    </div>
  );
};

export default OrganizationDetail;
